import * as THREE from 'three';
import { Layer, TShirtZone } from '../types';

const BLEED_PADDING_PX = 2;
const MIRROR_X = false;
const MIRROR_Y = false;
const OVERLAY_FILL = 'rgba(59, 130, 246, 0.22)';
const OVERLAY_STROKE = '#ef7218';
const OVERLAY_STROKE_WIDTH = 4;
const OVERLAY_BUTTON_PX = 34;
const OVERLAY_BORDER = '#1b1c15';
const OVERLAY_BG = '#ffffff';

const gradientRegex = /^linear-gradient\((\d+)deg,\s*(#[0-9a-fA-F]{6}),\s*(#[0-9a-fA-F]{6})(?:,\s*(\d{1,3})%)?\)$/;

const normalizeColorValue = (value?: string) => {
  if (!value) return undefined;
  const match = value.match(gradientRegex);
  return match ? match[2] : value;
};

const parseGradientValue = (value?: string) => {
  if (!value) return null;
  const match = value.match(gradientRegex);
  if (!match) return null;
  const stop = Math.min(100, Math.max(0, Number(match[4] ?? 50)));
  return {
    angle: Number(match[1]),
    start: match[2],
    end: match[3],
    stop,
  };
};

const buildGradientValue = (angle: number, start: string, end: string, stop = 50) =>
  `linear-gradient(${angle}deg, ${start}, ${end}, ${Math.min(100, Math.max(0, Math.round(stop)))}%)`;
const GUIDE_COLOR = 'rgba(255, 255, 255, 0.95)';
const GUIDE_OUTLINE = 'rgba(0, 0, 0, 0.9)';
const GUIDE_WIDTH = 2;
const GUIDE_OUTLINE_WIDTH = 4;
const MOTIF_WHITE_THRESHOLD = 210;
const MOTIF_BLACK_THRESHOLD = 20;
const MOTIF_MASK_WHITE_THRESHOLD = 245;

export type OverlayButton = 'pin' | 'rotate' | 'scale' | 'duplicate' | 'delete';

const iconCache: Record<string, HTMLImageElement> = {};
const motifMaskCache = new WeakMap<CanvasImageSource, HTMLCanvasElement>();
const motifTwoToneCache = new WeakMap<CanvasImageSource, Map<string, HTMLCanvasElement>>();
const motifDesignMaskCache = new WeakMap<CanvasImageSource, Map<string, HTMLCanvasElement>>();
const patternTempCanvasCache = new Map<string, { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; width: number; height: number }>();

function getPatternTempCanvas(key: string, width: number, height: number) {
  const existing = patternTempCanvasCache.get(key);
  if (existing && existing.width === width && existing.height === height) {
    return existing;
  }
  const canvas = existing?.canvas || document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const entry = { canvas, ctx, width, height };
  patternTempCanvasCache.set(key, entry);
  return entry;
}

function getMotifMask(source: CanvasImageSource): HTMLCanvasElement | null {
  if (motifMaskCache.has(source)) {
    return motifMaskCache.get(source) || null;
  }
  let width = 0;
  let height = 0;
  if (source instanceof HTMLImageElement) {
    width = source.naturalWidth;
    height = source.naturalHeight;
  } else if (source instanceof HTMLCanvasElement) {
    width = source.width;
    height = source.height;
  } else {
    return null;
  }
  if (!width || !height) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(source, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a === 0) continue;
    if (r >= MOTIF_MASK_WHITE_THRESHOLD && g >= MOTIF_MASK_WHITE_THRESHOLD && b >= MOTIF_MASK_WHITE_THRESHOLD) {
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  motifMaskCache.set(source, canvas);
  return canvas;
}

function getMotifDesignMask(
  source: CanvasImageSource,
  designKey: 'design1' | 'design2' | 'design'
): HTMLCanvasElement | null {
  const existing = motifDesignMaskCache.get(source);
  if (existing?.has(designKey)) {
    return existing.get(designKey) || null;
  }
  let width = 0;
  let height = 0;
  if (source instanceof HTMLImageElement) {
    width = source.naturalWidth;
    height = source.naturalHeight;
  } else if (source instanceof HTMLCanvasElement) {
    width = source.width;
    height = source.height;
  } else {
    return null;
  }
  if (!width || !height) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(source, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const isWhite = r >= MOTIF_WHITE_THRESHOLD && g >= MOTIF_WHITE_THRESHOLD && b >= MOTIF_WHITE_THRESHOLD;
    const isBlack = r <= MOTIF_BLACK_THRESHOLD && g <= MOTIF_BLACK_THRESHOLD && b <= MOTIF_BLACK_THRESHOLD;
    const gray = isNearlyGray(r, g, b);
    if (designKey === 'design') {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
      continue;
    }
    if (designKey === 'design1') {
      if (isWhite) {
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = 255;
        continue;
      }
      if (gray && !isBlack) {
        const luma = (r + g + b) / (255 * 3);
        const alphaScale = clamp01((luma - 0.45) / 0.55);
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = Math.round(a * alphaScale);
        continue;
      }
      data[i + 3] = 0;
      continue;
    }
    if (isBlack) {
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = 255;
      continue;
    }
    if (gray && !isWhite) {
      const luma = (r + g + b) / (255 * 3);
      const alphaScale = clamp01((0.55 - luma) / 0.55);
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(a * alphaScale);
      continue;
    }
    data[i + 3] = 0;
  }
  ctx.putImageData(imageData, 0, 0);

  const map = existing || new Map<string, HTMLCanvasElement>();
  map.set(designKey, canvas);
  motifDesignMaskCache.set(source, map);
  return canvas;
}

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }
  const int = parseInt(normalized, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));


const isNearlyGray = (r: number, g: number, b: number, tolerance = 24) =>
  Math.abs(r - g) <= tolerance && Math.abs(r - b) <= tolerance && Math.abs(g - b) <= tolerance;

// Returns a fast per-pixel sampler with all constants precomputed.
// Call once before the pixel loop; the returned function takes only (x, y).
const makeGradientSampler = (
  gradient: { angle: number; start: string; end: string; stop: number },
  width: number,
  height: number
): ((x: number, y: number) => { r: number; g: number; b: number }) | null => {
  const startRgb = hexToRgb(gradient.start);
  const endRgb = hexToRgb(gradient.end);
  if (!startRgb || !endRgb) return null;
  const radians = ((gradient.angle - 90) * Math.PI) / 180;
  const dx = Math.cos(radians);
  const dy = Math.sin(radians);
  const cx = width / 2;
  const cy = height / 2;
  const x0 = cx - dx * (width / 2);
  const y0 = cy - dy * (height / 2);
  const vx = dx * width;
  const vy = dy * height;
  const denom = vx * vx + vy * vy || 1;
  const offset = (gradient.stop - 50) / 50;
  const sr = startRgb.r; const sg = startRgb.g; const sb = startRgb.b;
  const er = endRgb.r;   const eg = endRgb.g;   const eb = endRgb.b;
  return (x: number, y: number) => {
    const raw = ((x - x0) * vx + (y - y0) * vy) / denom;
    const shifted = raw < 0 ? 0 : raw > 1 ? 1 : raw + offset < 0 ? 0 : raw + offset > 1 ? 1 : raw + offset;
    return {
      r: (sr + (er - sr) * shifted + 0.5) | 0,
      g: (sg + (eg - sg) * shifted + 0.5) | 0,
      b: (sb + (eb - sb) * shifted + 0.5) | 0,
    };
  };
};

function getMotifTwoTone(
  source: CanvasImageSource,
  color1: string,
  color2: string
): HTMLCanvasElement | null {
  const gradientA = parseGradientValue(color1);
  const gradientB = parseGradientValue(color2);
  const colorA = normalizeColorValue(color1) || color1;
  const colorB = normalizeColorValue(color2) || color2;
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);
  if ((!rgbA && !gradientA) || (!rgbB && !gradientB)) return null;

  let width = 0;
  let height = 0;
  if (source instanceof HTMLImageElement) {
    width = source.naturalWidth;
    height = source.naturalHeight;
  } else if (source instanceof HTMLCanvasElement) {
    width = source.width;
    height = source.height;
  } else {
    return null;
  }
  if (!width || !height) return null;

  const cacheKey = `${color1}-${color2}-w${MOTIF_WHITE_THRESHOLD}`;
  const existing = motifTwoToneCache.get(source);
  if (existing?.has(cacheKey)) {
    return existing.get(cacheKey) || null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(source, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  // Precompute samplers once — avoids re-parsing hex and recalculating trig per pixel.
  const samplerA = gradientA ? makeGradientSampler(gradientA, width, height) : null;
  const samplerB = gradientB ? makeGradientSampler(gradientB, width, height) : null;

  // Track px/py with counters — avoids division/modulo per pixel.
  let px = 0;
  let py = 0;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (a >= 10) {
      if (r >= MOTIF_WHITE_THRESHOLD && g >= MOTIF_WHITE_THRESHOLD && b >= MOTIF_WHITE_THRESHOLD) {
        if (samplerA) {
          const c = samplerA(px, py);
          data[i] = c.r; data[i + 1] = c.g; data[i + 2] = c.b;
        } else if (rgbA) {
          data[i] = rgbA.r; data[i + 1] = rgbA.g; data[i + 2] = rgbA.b;
        }
      } else if (r <= MOTIF_BLACK_THRESHOLD && g <= MOTIF_BLACK_THRESHOLD && b <= MOTIF_BLACK_THRESHOLD) {
        if (samplerB) {
          const c = samplerB(px, py);
          data[i] = c.r; data[i + 1] = c.g; data[i + 2] = c.b;
        } else if (rgbB) {
          data[i] = rgbB.r; data[i + 1] = rgbB.g; data[i + 2] = rgbB.b;
        }
      } else if (isNearlyGray(r, g, b)) {
        const cA = samplerA ? samplerA(px, py) : rgbA;
        const cB = samplerB ? samplerB(px, py) : rgbB;
        if (cA && cB) {
          const t = (r + g + b) / (255 * 3);
          const tc = t < 0 ? 0 : t > 1 ? 1 : t;
          data[i]     = (cB.r + (cA.r - cB.r) * tc + 0.5) | 0;
          data[i + 1] = (cB.g + (cA.g - cB.g) * tc + 0.5) | 0;
          data[i + 2] = (cB.b + (cA.b - cB.b) * tc + 0.5) | 0;
        }
      }
    }

    px++;
    if (px === width) { px = 0; py++; }
  }
  ctx.putImageData(imageData, 0, 0);

  const map = existing || new Map<string, HTMLCanvasElement>();
  map.set(cacheKey, canvas);
  motifTwoToneCache.set(source, map);
  return canvas;
}

export function toAppUv(uv: THREE.Vector2 | [number, number]): [number, number] {
  const u = Array.isArray(uv) ? uv[0] : uv.x;
  const v = Array.isArray(uv) ? uv[1] : uv.y;
  const nextU = MIRROR_X ? 1 - u : u;
  const nextV = MIRROR_Y ? 1 - v : v;
  return [nextU, nextV];
}

function getIcon(name: string): HTMLImageElement {
  if (!iconCache[name]) {
    const img = new Image();
    img.src = `/assets/icon/${name}`;
    iconCache[name] = img;
  }
  return iconCache[name];
}

export async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image load error'));
      if (event.target?.result) {
        img.src = event.target.result as string;
      }
    };

    reader.onerror = () => reject(new Error('File read error'));
    reader.readAsDataURL(file);
  });
}

export async function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load error'));
    img.src = url;
  });
}

export const toCanvasFontFamily = (fontFamily: string) => {
  const escaped = (fontFamily || 'Arial').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escaped}", Arial, sans-serif`;
};

export function createTextCanvas(textData: {
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  kind?: 'text' | 'number' | 'name';
  strokeColor?: string;
  strokeWidth?: number;
  canvasResolution?: number;
}): HTMLCanvasElement {
  const resolution = textData.canvasResolution || 1024;
  const canvas = document.createElement('canvas');
  canvas.width = resolution;
  canvas.height = resolution;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to get 2D context');
  }

  ctx.clearRect(0, 0, resolution, resolution);
  ctx.fillStyle = textData.color;
  ctx.font = `${textData.fontSize}px ${toCanvasFontFamily(textData.fontFamily)}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const lines = (textData.text || ' ').split('\n');
  const lineHeight = textData.fontSize * 1.3;
  const totalH = lines.length * lineHeight;
  const startY = (resolution - totalH) / 2 + lineHeight / 2;

  if (textData.strokeWidth && textData.strokeWidth > 0) {
    ctx.strokeStyle = textData.strokeColor || '#000000';
    ctx.lineWidth = textData.strokeWidth;
    ctx.lineJoin = 'round';
    lines.forEach((line, i) => ctx.strokeText(line || ' ', resolution / 2, startY + i * lineHeight));
  }
  lines.forEach((line, i) => ctx.fillText(line || ' ', resolution / 2, startY + i * lineHeight));

  return canvas;
}

export function getDefaultUvSize(
  width: number,
  height: number,
  maxUvSize = 0.25
): [number, number] {
  if (width <= 0 || height <= 0) {
    return [maxUvSize, maxUvSize];
  }
  const aspect = width / height;
  if (aspect >= 1) {
    return [maxUvSize, maxUvSize / aspect];
  }
  return [maxUvSize * aspect, maxUvSize];
}

function getLocalUv(layer: Layer, uv: [number, number]) {
  const du = uv[0] - layer.uvCenter[0];
  const dv = uv[1] - layer.uvCenter[1];
  const cos = Math.cos(-layer.uvRotation);
  const sin = Math.sin(-layer.uvRotation);
  return {
    u: du * cos - dv * sin,
    v: du * sin + dv * cos,
  };
}

function getOverlayLayout(layer: Layer, canvasWidth: number, canvasHeight: number) {
  const halfU = layer.uvSize[0] / 2;
  const halfV = layer.uvSize[1] / 2;
  const buttonU = OVERLAY_BUTTON_PX / canvasWidth;
  const buttonV = OVERLAY_BUTTON_PX / canvasHeight;
  const gapU = 0;
  const gapV = 0;
  const halfBtnU = buttonU / 2;
  const halfBtnV = buttonV / 2;

  return {
    buttonU,
    buttonV,
    pin: { u: -halfU - gapU - halfBtnU, v: halfV + gapV + halfBtnV },
    rotate: { u: halfU + gapU + halfBtnU, v: halfV + gapV + halfBtnV },
    scale: { u: halfU + gapU + halfBtnU, v: -halfV - gapV - halfBtnV },
    duplicate: { u: 0, v: halfV + gapV + halfBtnV },
    delete: { u: -halfU - gapU - halfBtnU, v: -halfV - gapV - halfBtnV },
  };
}

export function drawLayerToCanvas(
  ctx: CanvasRenderingContext2D,
  layer: Layer,
  allLayers: Layer[],
  canvasWidth: number,
  canvasHeight: number,
  zoneBounds?: Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number }>,
  motifTintColor?: string,
  materialBounds?: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>,
  opacityOverride?: number
): void {
  const [u, v] = layer.uvCenter;
  const [uvWidth, uvHeight] = layer.uvSize;

  const drawU = MIRROR_X ? 1 - u : u;
  const drawV = MIRROR_Y ? 1 - v : v;
  const x = drawU * canvasWidth;
  const y = drawV * canvasHeight;
  const pixelWidth = Math.max(1, uvWidth * canvasWidth);
  const pixelHeight = Math.max(1, uvHeight * canvasHeight);
  const drawWidth = Math.max(1, pixelWidth - BLEED_PADDING_PX * 2);
  const drawHeight = Math.max(1, pixelHeight - BLEED_PADDING_PX * 2);


  const drawMotif = (
    tintColor?: string,
    gradientBounds?: { minX: number; maxX: number; minY: number; maxY: number } | null,
    maskCanvas?: HTMLCanvasElement | null,
    overrideCenterX?: number,
    overrideCenterY?: number,
    overrideW?: number,
    overrideH?: number,
  ) => {
    const cx = overrideCenterX ?? x;
    const cy = overrideCenterY ?? y;
    const dw = overrideW ?? drawWidth;
    const dh = overrideH ?? drawHeight;
    ctx.translate(cx, cy);
    ctx.rotate(layer.uvRotation);
    if (MIRROR_X || MIRROR_Y) {
      ctx.scale(MIRROR_X ? -1 : 1, MIRROR_Y ? -1 : 1);
    }
    const designColors = layer.type === 'image' && layer.data?.isMotif
      ? ((layer.data as any)?.motifDesignColors as { design1?: string; design2?: string } | undefined)
      : undefined;
    const designGradients = layer.type === 'image' && layer.data?.isMotif
      ? ((layer.data as any)?.motifDesignGradients as
        | { design1?: { enabled: boolean; color2: string; angle: number; stop: number }; design2?: { enabled: boolean; color2: string; angle: number; stop: number } }
        | undefined)
      : undefined;
    const resolvedDesignColors = designColors || { design1: '#ffffff', design2: '#000000' };
    const design1 = designGradients?.design1?.enabled
      ? buildGradientValue(
          designGradients.design1.angle,
          resolvedDesignColors.design1 || '#ffffff',
          designGradients.design1.color2 || resolvedDesignColors.design1 || '#ffffff',
          designGradients.design1.stop
        )
      : resolvedDesignColors.design1 || '#ffffff';
    const design2 = designGradients?.design2?.enabled
      ? buildGradientValue(
          designGradients.design2.angle,
          resolvedDesignColors.design2 || '#000000',
          designGradients.design2.color2 || resolvedDesignColors.design2 || '#000000',
          designGradients.design2.stop
        )
      : resolvedDesignColors.design2 || '#000000';
    const isPatternMotif =
      layer.type === 'image' && Boolean(layer.data?.isMotif && (layer.data as any)?.isPatternMotif);
    const patternColors = isPatternMotif
      ? ((layer.data as any)?.motifPatternColors as { motif1?: string; motif2?: string } | undefined)
      : undefined;
    const hasDesignColors =
      layer.type === 'image' &&
      layer.data?.isMotif &&
      Boolean((layer.data as any)?.motifDesignColors);
    const isGradientTint = Boolean(tintColor && parseGradientValue(tintColor));
    const patternTwoToneSource =
      isPatternMotif && patternColors
        ? getMotifTwoTone(
          layer.source,
          patternColors.motif2 || '#ffffff',
          patternColors.motif1 || '#000000'
        )
        : null;
    const usedPatternTwoTone = Boolean(patternTwoToneSource);
    const twoToneSource =
      layer.type === 'image' && layer.data?.isMotif && hasDesignColors
        ? getMotifTwoTone(
          layer.source,
          design2,
          design1
        )
        : null;
    const sourceToDraw =
      patternTwoToneSource ||
      (layer.type === 'image' && layer.data?.isMotif && tintColor && !hasDesignColors && !isGradientTint && !isPatternMotif
        ? getMotifMask(layer.source) || layer.source
        : twoToneSource || layer.source);
    ctx.drawImage(sourceToDraw, -dw / 2, -dh / 2, dw, dh);
    if (tintColor && !hasDesignColors && !(isPatternMotif && usedPatternTwoTone)) {
      ctx.globalCompositeOperation = 'source-atop';
      const gradient = parseGradientValue(tintColor);
      if (gradient) {
        const bounds = gradientBounds || {
          minX: -dw / 2,
          maxX: dw / 2,
          minY: -dh / 2,
          maxY: dh / 2,
        };
        const width = Math.max(1, bounds.maxX - bounds.minX);
        const height = Math.max(1, bounds.maxY - bounds.minY);
        const centerX = (bounds.minX + bounds.maxX) / 2;
        const centerY = (bounds.minY + bounds.maxY) / 2;
        const radians = ((gradient.angle - 90) * Math.PI) / 180;
        const dx = Math.cos(radians);
        const dy = Math.sin(radians);
        const x0 = centerX - dx * (width / 2);
        const y0 = centerY - dy * (height / 2);
        const x1 = centerX + dx * (width / 2);
        const y1 = centerY + dy * (height / 2);
        const paint = ctx.createLinearGradient(x0, y0, x1, y1);
        const offset = Math.min(1, Math.max(-1, (gradient.stop - 50) / 50));
        const stopA = clamp01(0 + offset);
        const stopB = clamp01(1 + offset);
        paint.addColorStop(stopA, gradient.start);
        paint.addColorStop(stopB, gradient.end);
        ctx.fillStyle = paint;
      } else {
        ctx.fillStyle = tintColor;
      }
      ctx.fillRect(-dw / 2, -dh / 2, dw, dh);
      ctx.globalCompositeOperation = 'source-over';
    }
    if (maskCanvas) {
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(maskCanvas, -dw / 2, -dh / 2, dw, dh);
      ctx.globalCompositeOperation = 'source-over';
    }
  };

  if (layer.type === 'image' && layer.data?.isMotif) {
    const isGlobalUv = Boolean((layer.data as any)?.isGlobalUv);
    const selectedMaterialIds = (layer.data as any)?.selectedMaterialIds as string[] | undefined;
    const activeSelectedIds = selectedMaterialIds && selectedMaterialIds.length > 0 ? selectedMaterialIds : undefined;
    const motifMaterialColors = (layer.data as any)?.motifMaterialColors as Record<string, string> | undefined;
    const isPatternMotif = Boolean((layer.data as any)?.isPatternMotif);
    const patternDesignKey = (layer.data as any)?.patternDesignKey as 'design1' | 'design2' | 'design' | undefined;
    const baseDesignLayer =
      patternDesignKey
        ? allLayers.find(item => item.data?.isMotif && !(item.data as any)?.isPatternMotif)
        : null;
    const patternMask = patternDesignKey && baseDesignLayer
      ? getMotifDesignMask(baseDesignLayer.source, patternDesignKey)
      : null;
    if (isGlobalUv) {
      ctx.save();
      drawMotif();
      ctx.restore();
      return;
    }
    if (patternDesignKey && patternMask) {
      // Use design layer dimensions so the mask aligns correctly at full canvas scale
      const designCX = (baseDesignLayer?.uvCenter[0] ?? 0.5) * canvasWidth;
      const designCY = (baseDesignLayer?.uvCenter[1] ?? 0.5) * canvasHeight;
      const designW = Math.max(1, (baseDesignLayer?.uvSize[0] ?? 1) * canvasWidth - BLEED_PADDING_PX * 2);
      const designH = Math.max(1, (baseDesignLayer?.uvSize[1] ?? 1) * canvasHeight - BLEED_PADDING_PX * 2);

      const tempEntry = getPatternTempCanvas(layer.id, designW, designH);
      if (!tempEntry) return;
      const { canvas: tempCanvas, ctx: tempCtx } = tempEntry;
      tempCtx.setTransform(1, 0, 0, 1, 0, 0);
      tempCtx.clearRect(0, 0, designW, designH);

      const patternColors = (layer.data as any)?.motifPatternColors as
        | { motif1?: string; motif2?: string }
        | undefined;
      const patternSource = patternColors
        ? getMotifTwoTone(
          layer.source,
          patternColors.motif2 || '#ffffff',
          patternColors.motif1 || '#000000'
        ) || layer.source
        : layer.source;

      tempCtx.drawImage(patternSource, 0, 0, designW, designH);
      tempCtx.globalCompositeOperation = 'destination-in';
      tempCtx.drawImage(patternMask, 0, 0, designW, designH);
      tempCtx.globalCompositeOperation = 'source-over';

      const drawMasked = () => {
        ctx.translate(designCX, designCY);
        ctx.rotate(layer.uvRotation);
        if (MIRROR_X || MIRROR_Y) {
          ctx.scale(MIRROR_X ? -1 : 1, MIRROR_Y ? -1 : 1);
        }
        ctx.drawImage(tempCanvas, -designW / 2, -designH / 2, designW, designH);
      };

      if (activeSelectedIds && materialBounds) {
        let drew = false;
        activeSelectedIds.forEach(materialId => {
          const boundsList = materialBounds[materialId] || [];
          if (boundsList.length === 0) return;
          ctx.save();
          ctx.beginPath();
          boundsList.forEach(bounds => {
            ctx.rect(
              bounds.uMin * canvasWidth,
              bounds.vMin * canvasHeight,
              (bounds.uMax - bounds.uMin) * canvasWidth,
              (bounds.vMax - bounds.vMin) * canvasHeight
            );
          });
          ctx.clip();
          drawMasked();
          ctx.restore();
          drew = true;
        });
        if (drew) return;
      }

      ctx.save();
      drawMasked();
      ctx.restore();
      return;
    }
    if (activeSelectedIds && materialBounds) {
      let drew = false;
      activeSelectedIds.forEach(materialId => {
        const boundsList = materialBounds[materialId] || [];
        if (boundsList.length === 0) return;
        // Compute the overall bounding box of all UV islands for this material
        const overallUMin = Math.min(...boundsList.map(b => b.uMin));
        const overallUMax = Math.max(...boundsList.map(b => b.uMax));
        const overallVMin = Math.min(...boundsList.map(b => b.vMin));
        const overallVMax = Math.max(...boundsList.map(b => b.vMax));
        const matCenterX = ((overallUMin + overallUMax) / 2) * canvasWidth;
        const matCenterY = ((overallVMin + overallVMax) / 2) * canvasHeight;
        const matW = (overallUMax - overallUMin) * canvasWidth;
        const matH = (overallVMax - overallVMin) * canvasHeight;
        // Gradient bounds in local space (symmetric around material center)
        const matGradientBounds = { minX: -matW / 2, maxX: matW / 2, minY: -matH / 2, maxY: matH / 2 };
        ctx.save();
        ctx.beginPath();
        boundsList.forEach(bounds => {
          ctx.rect(
            bounds.uMin * canvasWidth,
            bounds.vMin * canvasHeight,
            (bounds.uMax - bounds.uMin) * canvasWidth,
            (bounds.vMax - bounds.vMin) * canvasHeight
          );
        });
        ctx.clip();
        const tint = isPatternMotif ? undefined : motifMaterialColors?.[materialId];
        drawMotif(tint, matGradientBounds, patternMask, matCenterX, matCenterY, matW, matH);
        ctx.restore();
        drew = true;
      });
      if (drew) {
        return;
      }
    }
  }

  ctx.save();
  const effectiveOpacity = opacityOverride ?? layer.opacity ?? 1;
  if (effectiveOpacity < 1) {
    ctx.globalAlpha = Math.max(0, effectiveOpacity);
  }
  const fallbackTint =
    layer.type === 'image' && layer.data?.isMotif
      ? ((layer.data as any)?.motifMaterialColors ? undefined : motifTintColor)
      : undefined;
  drawMotif(fallbackTint);
  ctx.restore();
}

export function drawSelectionOverlay(
  ctx: CanvasRenderingContext2D,
  layer: Layer,
  canvasWidth: number,
  canvasHeight: number
): boolean {
  const [u, v] = layer.uvCenter;
  const [uvWidth, uvHeight] = layer.uvSize;
  const drawU = MIRROR_X ? 1 - u : u;
  const drawV = MIRROR_Y ? 1 - v : v;
  const x = drawU * canvasWidth;
  const y = drawV * canvasHeight;
  const pixelWidth = Math.max(1, uvWidth * canvasWidth - BLEED_PADDING_PX * 2);
  const pixelHeight = Math.max(1, uvHeight * canvasHeight - BLEED_PADDING_PX * 2);

  const layout = getOverlayLayout(layer, canvasWidth, canvasHeight);
  const buttonPx = OVERLAY_BUTTON_PX;
  const iconSize = Math.max(14, buttonPx * 0.6);
  let iconsReady = true;

  ctx.save();
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.translate(x, y);
  ctx.rotate(layer.uvRotation);
  if (MIRROR_X || MIRROR_Y) {
    ctx.scale(MIRROR_X ? -1 : 1, MIRROR_Y ? -1 : 1);
  }

  // 1. Semi-transparent blue fill
  ctx.fillStyle = OVERLAY_FILL;
  ctx.fillRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);

  // 2. Outer dark halo — visible against any background colour
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.lineWidth = OVERLAY_STROKE_WIDTH + 4;
  ctx.strokeRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);

  // 3. White inner stroke — creates contrast with the halo
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.lineWidth = OVERLAY_STROKE_WIDTH + 1;
  ctx.strokeRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);

  // 4. Orange accent on top — the brand colour
  ctx.strokeStyle = OVERLAY_STROKE;
  ctx.lineWidth = OVERLAY_STROKE_WIDTH;
  ctx.strokeRect(-pixelWidth / 2, -pixelHeight / 2, pixelWidth, pixelHeight);

  const drawButton = (
    pos: { u: number; v: number },
    iconName: string,
    isActive?: boolean
  ) => {
    const bx = pos.u * canvasWidth;
    const by = pos.v * canvasHeight;
    const half = buttonPx / 2;
    ctx.fillStyle = isActive ? '#ee7218' : OVERLAY_BG;
    ctx.fillRect(bx - half, by - half, buttonPx, buttonPx);
    ctx.strokeStyle = OVERLAY_BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(bx - half, by - half, buttonPx, buttonPx);

    const icon = getIcon(iconName);
    if (icon.complete && icon.naturalWidth > 0) {
      ctx.drawImage(icon, bx - iconSize / 2, by - iconSize / 2, iconSize, iconSize);
    } else {
      iconsReady = false;
    }
  };

  drawButton(layout.pin, 'icons8-pin-50.png', layer.locked);
  drawButton(layout.rotate, 'icons8-rotate-50.png');
  drawButton(layout.scale, 'icons8-enlarge-50.png');
  drawButton(layout.duplicate, 'icons8-dupliquer-50.png');
  drawButton(layout.delete, 'icons8-full-trash-50.png');

  ctx.restore();
  return iconsReady;
}

export function hitTestOverlayButton(
  uv: [number, number],
  layer: Layer,
  canvasWidth: number,
  canvasHeight: number
): OverlayButton | null {
  const local = getLocalUv(layer, uv);
  if (MIRROR_X) {
    local.u = -local.u;
  }
  if (MIRROR_Y) {
    local.v = -local.v;
  }
  const layout = getOverlayLayout(layer, canvasWidth, canvasHeight);
  const halfU = layout.buttonU / 2;
  const halfV = layout.buttonV / 2;

  const hit = (pos: { u: number; v: number }) =>
    Math.abs(local.u - pos.u) <= halfU && Math.abs(local.v - pos.v) <= halfV;

  if (hit(layout.pin)) return 'pin';
  if (hit(layout.rotate)) return 'rotate';
  if (hit(layout.scale)) return 'scale';
  if (hit(layout.duplicate)) return 'duplicate';
  if (hit(layout.delete)) return 'delete';
  return null;
}

export type LayerDragOverride = {
  id: string;
  uvCenter?: [number, number];
  uvSize?: [number, number];
  uvRotation?: number;
};

export function renderLayersToCanvas(
  ctx: CanvasRenderingContext2D,
  layers: Layer[],
  canvasWidth: number,
  canvasHeight: number,
  backgroundColor?: string,
  zoneBounds?: Record<TShirtZone, { uMin: number; uMax: number; vMin: number; vMax: number }>,
  motifTintColor?: string,
  materialBounds?: Record<string, { uMin: number; uMax: number; vMin: number; vMax: number }[]>,
  preserveCanvas?: boolean,
  layerOverride?: LayerDragOverride | null,
  layerOpacityOverrides?: Record<string, number>
): void {
  if (!preserveCanvas) {
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    } else {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    }
  }

  // Motif/design layers always render first (behind everything), regardless of stored order
  const sortedLayers = [...layers]
    .filter(layer => !(layer.data as any)?.isSystem)
    .sort((a, b) => {
      const aIsMotif = (a.data as any)?.isMotif ? -1 : 0;
      const bIsMotif = (b.data as any)?.isMotif ? -1 : 0;
      if (aIsMotif !== bIsMotif) return aIsMotif - bIsMotif;
      return a.order - b.order;
    });
  sortedLayers.forEach(layer => {
    const effective =
      layerOverride && layer.id === layerOverride.id
        ? {
            ...layer,
            uvCenter: layerOverride.uvCenter ?? layer.uvCenter,
            uvSize: layerOverride.uvSize ?? layer.uvSize,
            uvRotation: layerOverride.uvRotation ?? layer.uvRotation,
          }
        : layer;
    drawLayerToCanvas(
      ctx,
      effective,
      layers,
      canvasWidth,
      canvasHeight,
      zoneBounds,
      motifTintColor,
      materialBounds,
      layerOpacityOverrides?.[layer.id]
    );
  });
}

export function drawAlignmentGuides(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  guide: {
    u?: number | null;
    v?: number | null;
    bounds?: { uMin: number; uMax: number; vMin: number; vMax: number } | null;
  } | null
): void {
  if (!guide) return;

  let uMin = 0;
  let uMax = 1;
  let vMin = 0;
  let vMax = 1;
  if (guide.bounds) {
    const bounds = guide.bounds;
    uMin = bounds.uMin;
    uMax = bounds.uMax;
    vMin = bounds.vMin;
    vMax = bounds.vMax;
  }

  if (MIRROR_X) {
    const nextUMin = 1 - uMax;
    const nextUMax = 1 - uMin;
    uMin = nextUMin;
    uMax = nextUMax;
  }
  if (MIRROR_Y) {
    const nextVMin = 1 - vMax;
    const nextVMax = 1 - vMin;
    vMin = nextVMin;
    vMax = nextVMax;
  }

  const xMin = uMin * canvasWidth;
  const xMax = uMax * canvasWidth;
  const yMin = vMin * canvasHeight;
  const yMax = vMax * canvasHeight;

  ctx.save();
  const strokeLine = (draw: () => void) => {
    ctx.strokeStyle = GUIDE_OUTLINE;
    ctx.lineWidth = GUIDE_OUTLINE_WIDTH;
    draw();
    ctx.strokeStyle = GUIDE_COLOR;
    ctx.lineWidth = GUIDE_WIDTH;
    draw();
  };

  if (guide.u !== null && guide.u !== undefined) {
    const guideU = MIRROR_X ? 1 - guide.u : guide.u;
    const x = guideU * canvasWidth;
    strokeLine(() => {
      ctx.beginPath();
      ctx.moveTo(x, yMin);
      ctx.lineTo(x, yMax);
      ctx.stroke();
    });
  }

  if (guide.v !== null && guide.v !== undefined) {
    const guideV = MIRROR_Y ? 1 - guide.v : guide.v;
    const y = guideV * canvasHeight;
    strokeLine(() => {
      ctx.beginPath();
      ctx.moveTo(xMin, y);
      ctx.lineTo(xMax, y);
      ctx.stroke();
    });
  }

  ctx.restore();
}

export async function createTextureFromFile(file: File): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const texture = new THREE.Texture(img);
        texture.needsUpdate = true;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
        resolve(texture);
      };

      img.onerror = () => {
        reject(new Error('Image load error'));
      };

      if (event.target?.result) {
        img.src = event.target.result as string;
      }
    };

    reader.onerror = () => {
      reject(new Error('File read error'));
    };

    reader.readAsDataURL(file);
  });
}

export function createTextTexture(textData: {
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
  canvasResolution?: number;
}): THREE.CanvasTexture {
  const resolution = textData.canvasResolution || 1024;
  const canvas = document.createElement('canvas');
  canvas.width = resolution;
  canvas.height = resolution;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to get 2D context');
  }

  ctx.fillStyle = 'rgba(0, 0, 0, 0)';
  ctx.fillRect(0, 0, resolution, resolution);

  ctx.fillStyle = textData.color;
  ctx.font = `${textData.fontSize}px ${toCanvasFontFamily(textData.fontFamily)}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(textData.text, resolution / 2, resolution / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;

  return texture;
}

export function findMainMesh(object: THREE.Object3D): THREE.Mesh | null {
  if (object instanceof THREE.Mesh) {
    return object;
  }

  for (const child of object.children) {
    const mesh = findMainMesh(child);
    if (mesh) {
      return mesh;
    }
  }

  return null;
}

export function findTShirtMeshes(object: THREE.Object3D): Record<TShirtZone, THREE.Mesh | null> {
  const meshes: Record<TShirtZone, THREE.Mesh | null> = {
    front: null,
    back: null,
    leftSleeve: null,
    rightSleeve: null,
  };

  function traverseObject(obj: THREE.Object3D) {
    if (obj instanceof THREE.Mesh) {
      const meshName = obj.name;

      if (meshName === 'Tshirt_Front' || meshName.includes('Front')) {
        meshes.front = obj;
      } else if (meshName === 'Tshirt_Back' || meshName.includes('Back')) {
        meshes.back = obj;
      } else if (meshName === 'Tshirt_LeftSleeve' || meshName.includes('LeftSleeve')) {
        meshes.leftSleeve = obj;
      } else if (meshName === 'Tshirt_RightSleeve' || meshName.includes('RightSleeve')) {
        meshes.rightSleeve = obj;
      }
    }

    obj.children.forEach(child => traverseObject(child));
  }

  traverseObject(object);

  const hasMesh = Object.values(meshes).some(mesh => mesh !== null);
  if (!hasMesh) {
    const mainMesh = findMainMesh(object);
    if (mainMesh) {
      meshes.front = mainMesh;
    }
  }

  return meshes;
}

export function getZoneFromMesh(mesh: THREE.Mesh): TShirtZone | null {
  const meshName = mesh.name;

  if (meshName === 'Tshirt_Front' || meshName.includes('Front')) {
    return 'front';
  } else if (meshName === 'Tshirt_Back' || meshName.includes('Back')) {
    return 'back';
  } else if (meshName === 'Tshirt_LeftSleeve' || meshName.includes('LeftSleeve')) {
    return 'leftSleeve';
  } else if (meshName === 'Tshirt_RightSleeve' || meshName.includes('RightSleeve')) {
    return 'rightSleeve';
  }

  return null;
}

export function configureTextureForDecal(texture: THREE.Texture): void {
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
}

export function disposeTexture(texture: THREE.Texture): void {
  texture.dispose();
  if (texture.image instanceof HTMLImageElement) {
    URL.revokeObjectURL(texture.image.src);
  }
}

export function calculateDecalTransform(
  _mesh: THREE.Mesh,
  point: THREE.Vector3,
  normal: THREE.Vector3
): { position: THREE.Vector3; rotation: THREE.Euler; scale: THREE.Vector3 } {
  const position = point.clone();
  const rotation = new THREE.Euler().setFromQuaternion(
    new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal)
  );
  const scale = new THREE.Vector3(0.1, 0.1, 0.1);

  return { position, rotation, scale };
}
